import React, { useState, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Typography,
  IconButton,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  Button,
} from '@mui/material';
import {
  MoreVert,
  GetApp,
  Visibility,
  Edit,
  Delete,
  History,
  Payment,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';

const DataTable = ({
  data = [],
  columns = [],
  actions = [],
  onAction,
  itemsPerPage = 50,
  showPagination = true,
  showExport = true,
  showPDFExport = true,
  exportFileName = 'data',
  onExport,
  getClient,
  getInitials,
  formatPrice,
  formatDate,
  statusOptions = [],
  getStatusColor,
  emptyStateIcon: EmptyStateIcon,
  emptyStateTitle = 'No data yet',
  emptyStateSubtitle = 'Create your first item to get started',
  searchEmptyStateTitle = 'No items found matching your search',
  searchEmptyStateSubtitle = 'Try adjusting your search terms',
  hasSearchResults = false,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(itemsPerPage);
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);

  // Sorting logic
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createSortHandler = (property) => () => {
    handleRequestSort(property);
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!orderBy) return data;

    return [...data].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Handle nested properties (e.g., 'client.first_name')
      if (orderBy.includes('.')) {
        const keys = orderBy.split('.');
        aValue = keys.reduce((obj, key) => obj?.[key], a);
        bValue = keys.reduce((obj, key) => obj?.[key], b);
      }

      // Handle special cases
      if (orderBy === 'client') {
        const clientA = getClient ? getClient(a.client_id) : null;
        const clientB = getClient ? getClient(b.client_id) : null;
        aValue = clientA ? `${clientA.first_name} ${clientA.last_name}` : '';
        bValue = clientB ? `${clientB.first_name} ${clientB.last_name}` : '';
      }

      if (orderBy === 'amount') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      if (orderBy === 'date' || orderBy.includes('date') || orderBy.includes('created') || orderBy.includes('updated')) {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, orderBy, order, getClient]);

  // Pagination logic
  const paginatedData = useMemo(() => {
    if (!showPagination) return sortedData;
    const startIndex = page * rowsPerPage;
    return sortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedData, page, rowsPerPage, showPagination]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Action menu logic
  const handleMenuOpen = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const handleAction = (actionType) => {
    if (onAction && selectedItem) {
      onAction(actionType, selectedItem);
    }
    handleMenuClose();
  };

  // Export functions
  const exportToExcel = () => {
    try {
      if (!sortedData || sortedData.length === 0) {
        throw new Error('No data to export. Please check your filters.');
      }

      // Prepare data for export
      const exportData = sortedData.map(item => {
        const exportRow = {};
        columns.forEach(column => {
          if (column.exportable !== false) {
            let value = item[column.field];
            
            // Handle special formatting
            if (column.type === 'client' && getClient) {
              const client = getClient(item.client_id);
              value = client ? `${client.first_name} ${client.last_name}` : 'Unknown Client';
            } else if (column.type === 'date' && value) {
              value = formatDate ? formatDate(value) : new Date(value).toLocaleDateString();
            } else if (column.type === 'amount' && value) {
              value = formatPrice ? formatPrice(value) : value;
            } else if (column.type === 'status' && statusOptions.length > 0) {
              const statusOption = statusOptions.find(opt => opt.value === value);
              value = statusOption ? statusOption.label : value;
            }
            
            exportRow[column.header] = value || '';
          }
        });
        return exportRow;
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      
      // Auto-size columns
      const columnWidths = columns
        .filter(col => col.exportable !== false)
        .map(col => ({ wch: col.exportWidth || 15 }));
      worksheet['!cols'] = columnWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
      
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `${exportFileName}_${currentDate}.xlsx`;
      
      XLSX.writeFile(workbook, filename);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    }
  };

  const exportToPDF = () => {
    try {
      if (!sortedData || sortedData.length === 0) {
        throw new Error('No data to export. Please check your filters.');
      }

      const doc = new jsPDF('landscape'); // Use landscape for better table display
      
      // Add title
      doc.setFontSize(20);
      doc.text(`${exportFileName.charAt(0).toUpperCase() + exportFileName.slice(1)} Report`, 20, 20);
      
      // Add export info
      doc.setFontSize(12);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 35);
      doc.text(`Total Items: ${sortedData.length}`, 20, 45);
      
      // Filter exportable columns
      const exportableColumns = columns.filter(col => col.type !== 'actions' && col.exportable !== false);
      
      // Calculate column widths
      const pageWidth = doc.internal.pageSize.width - 40; // 20px margin on each side
      const columnWidth = pageWidth / exportableColumns.length;
      
      let yPosition = 65;
      const rowHeight = 8;
      const headerHeight = 12;
      
      // Add table header
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      exportableColumns.forEach((column, index) => {
        const xPosition = 20 + (index * columnWidth);
        doc.text(column.header, xPosition, yPosition);
      });
      
      // Draw header line
      yPosition += 2;
      doc.line(20, yPosition, pageWidth + 20, yPosition);
      yPosition += 5;
      
      // Add table data
      doc.setFont(undefined, 'normal');
      sortedData.forEach((item, rowIndex) => {
        // Check if we need a new page
        if (yPosition > 180) { // Leave space for footer
          doc.addPage();
          yPosition = 20;
          
          // Redraw header on new page
          doc.setFont(undefined, 'bold');
          exportableColumns.forEach((column, index) => {
            const xPosition = 20 + (index * columnWidth);
            doc.text(column.header, xPosition, yPosition);
          });
          yPosition += 2;
          doc.line(20, yPosition, pageWidth + 20, yPosition);
          yPosition += 5;
          doc.setFont(undefined, 'normal');
        }
        
        // Add row data
        exportableColumns.forEach((column, colIndex) => {
          let value = item[column.field];
          
          // Format values based on column type
          if (column.type === 'client' && getClient) {
            const client = getClient(item.client_id);
            value = client ? `${client.first_name} ${client.last_name}` : 'Unknown Client';
          } else if (column.type === 'date' && value) {
            value = formatDate ? formatDate(value) : new Date(value).toLocaleDateString();
          } else if (column.type === 'amount' && value) {
            value = formatPrice ? formatPrice(value) : value;
          } else if (column.type === 'status' && statusOptions.length > 0) {
            const statusOption = statusOptions.find(opt => opt.value === value);
            value = statusOption ? statusOption.label : value;
          }
          
          const xPosition = 20 + (colIndex * columnWidth);
          const displayValue = value || 'N/A';
          
          // Truncate long text to fit column width
          const maxChars = Math.floor(columnWidth / 2.5); // Approximate chars per unit width
          const truncatedValue = displayValue.length > maxChars 
            ? displayValue.substring(0, maxChars - 3) + '...' 
            : displayValue;
          
          doc.text(truncatedValue, xPosition, yPosition);
        });
        
        yPosition += rowHeight;
      });
      
      // Add summary at the bottom
      yPosition += 10;
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text(`Total Records: ${sortedData.length}`, 20, yPosition);
      
      // Add totals for amount columns if applicable
      const amountColumns = exportableColumns.filter(col => col.type === 'amount');
      if (amountColumns.length > 0) {
        yPosition += 8;
        amountColumns.forEach(column => {
          const total = calculateTotal(column.field);
          const formattedTotal = formatPrice ? formatPrice(total) : total;
          doc.text(`Total ${column.header}: ${formattedTotal}`, 20, yPosition);
          yPosition += 6;
        });
      }
      
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `${exportFileName}_${currentDate}.pdf`;
      
      doc.save(filename);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
    }
  };

  // Calculate total for amount columns
  const calculateTotal = (field) => {
    return sortedData.reduce((sum, item) => {
      const value = parseFloat(item[field]) || 0;
      return sum + value;
    }, 0);
  };

  // Render cell content based on column type
  const renderCell = (item, column) => {
    const value = item[column.field];

    switch (column.type) {
      case 'client':
        const client = getClient ? getClient(item.client_id) : null;
        return (
          <Box display="flex" alignItems="center">
            <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
              {client ? getInitials(client.first_name, client.last_name) : '?'}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                {client ? `${client.first_name} ${client.last_name}` : 'Unknown Client'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {client?.mobile || 'No mobile'}
              </Typography>
            </Box>
          </Box>
        );

      case 'amount':
        return (
          <Typography variant="subtitle2" fontWeight="bold">
            {formatPrice ? formatPrice(value) : value}
          </Typography>
        );

      case 'date':
        return (
          <Typography variant="body2">
            {formatDate ? formatDate(value) : new Date(value).toLocaleDateString()}
          </Typography>
        );

      case 'status':
        const statusOption = statusOptions.find(opt => opt.value === value);
        return (
          <Chip 
            label={statusOption ? statusOption.label : value}
            color={getStatusColor ? getStatusColor(value) : 'default'}
            size="small"
          />
        );

      case 'actions':
        return (
          <IconButton onClick={(e) => handleMenuOpen(e, item)}>
            <MoreVert />
          </IconButton>
        );

      default:
        return (
          <Typography variant="subtitle2" fontWeight="bold">
            {value}
          </Typography>
        );
    }
  };

  return (
    <Box>
      {/* Export Button */}
      {showExport && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<GetApp />}
            onClick={(event) => setExportAnchorEl(event.currentTarget)}
            disabled={sortedData.length === 0}
            size="small"
          >
            Export
          </Button>
        </Box>
      )}

      {/* Export Menu */}
      <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={() => setExportAnchorEl(null)}
      >
        <MenuItem onClick={() => {
          exportToExcel();
          setExportAnchorEl(null);
        }}>
          <GetApp sx={{ mr: 1 }} /> Export to Excel
        </MenuItem>
        {showPDFExport && (
          <MenuItem onClick={() => {
            exportToPDF();
            setExportAnchorEl(null);
          }}>
            <GetApp sx={{ mr: 1 }} /> Export to PDF
          </MenuItem>
        )}
      </Menu>

      {/* Data Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.field}
                  align={column.align || 'left'}
                  sortDirection={orderBy === column.field ? order : false}
                >
                  {column.sortable !== false ? (
                    <TableSortLabel
                      active={orderBy === column.field}
                      direction={orderBy === column.field ? order : 'asc'}
                      onClick={createSortHandler(column.field)}
                    >
                      {column.header}
                    </TableSortLabel>
                  ) : (
                    column.header
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Box py={4}>
                    {EmptyStateIcon && <EmptyStateIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />}
                    <Typography variant="h6" color="text.secondary">
                      {hasSearchResults ? searchEmptyStateTitle : emptyStateTitle}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {hasSearchResults ? searchEmptyStateSubtitle : emptyStateSubtitle}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {paginatedData.map((item, index) => (
                  <TableRow key={item.id || index} hover>
                    {columns.map((column) => (
                      <TableCell key={column.field} align={column.align || 'left'}>
                        {renderCell(item, column)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                
                {/* Total Row for amount columns */}
                {paginatedData.length > 0 && columns.some(col => col.type === 'amount') && (
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    {columns.map((column, index) => {
                      if (column.type === 'amount') {
                        return (
                          <TableCell key={column.field} align={column.align || 'right'}>
                            <Typography variant="subtitle1" fontWeight="bold" color="primary">
                              {formatPrice ? formatPrice(calculateTotal(column.field)) : calculateTotal(column.field)}
                            </Typography>
                          </TableCell>
                        );
                      } else if (column.type === 'actions') {
                        // Skip actions column in totals row
                        return <TableCell key={column.field}></TableCell>;
                      } else if (index === 0) {
                        // Only show "Total" label in the first non-amount, non-actions column
                        const nonAmountNonActionColumns = columns.filter(col => col.type !== 'amount' && col.type !== 'actions');
                        return (
                          <TableCell key="total-label" colSpan={nonAmountNonActionColumns.length}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Total
                            </Typography>
                          </TableCell>
                        );
                      }
                      return null;
                    })}
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {showPagination && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={sortedData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {actions
          .filter((action) => {
            // Optional per-row visibility control
            if (typeof action?.condition === 'function') {
              try {
                return selectedItem ? Boolean(action.condition(selectedItem)) : false;
              } catch (e) {
                return false;
              }
            }
            return true;
          })
          .map((action) => (
            <MenuItem key={action.type} onClick={() => handleAction(action.type)}>
              {action.icon} {action.label}
            </MenuItem>
          ))}
      </Menu>
    </Box>
  );
};

export default DataTable;
